import type { IndustryModuleType, ResultVO, RoleAuthStrategy, IndustryCategory } from "@/api"
import { API_AUTH, isNH } from "@/constants/config"
import request from "@/http"
import http from "@/http/http"
// ==============================================
export interface CompanyRoleVO {
  id: string
  name: string
  desc: string
  // 权限
  authStrategy: RoleAuthStrategy[]
  moduleTypes: IndustryModuleType[]
}
// 获取公司角色列表
export async function getRoleList(brandId: string): Promise<CompanyRoleVO[]> {
  try {
    const result = await request.get<CompanyRoleVO[]>("/v1/industry/role", {
      params: {
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface CompanyRoleDTO {
  id?: string
  brandId: string
  name: string
  desc: string
  // 权限
  authStrategy: RoleAuthStrategy[]
  moduleTypes: IndustryModuleType[]
}
// 更新公司角色
export async function updateRole(params: CompanyRoleDTO): Promise<boolean> {
  try {
    const result = await request.patch<boolean>("/v1/industry/role", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 创建公司角色
export async function createRole(params: CompanyRoleDTO): Promise<boolean> {
  try {
    const result = await request.post<boolean>("/v1/industry/role", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface DeleteRoleArgs {
  companyId: number
  brandId: string
  id: string
}

// 删除角色
export async function deleteRole(args: DeleteRoleArgs): Promise<boolean> {
  try {
    const { companyId, brandId, id } = args
    const result = await request.delete<boolean>(
      `/v1/industry/role?id=${id}&companyId=${companyId}&brandId=${brandId}`,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CompanyOrgVO {
  id: string
  name: string
  userCount: number
  createAt: string
}
// 获取公司组织列表
export async function getOrgList(brandId: string): Promise<CompanyOrgVO[]> {
  try {
    const result = await request.get<CompanyOrgVO[]>("/v1/industry/org", {
      params: {
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface CompanyOrgDTO {
  orgId: string
  name: string
  brandId: string
}
// 更新公司组织信息
export async function updateOrg({ orgId, name, brandId }: CompanyOrgDTO): Promise<boolean> {
  try {
    const result = await request.patch<boolean>(
      `/v1/industry/org/${orgId}?name=${name}&brandId=${brandId}`,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

// 删除公司组织信息
export async function deleteOrg(orgId: string, brandId: string): Promise<boolean> {
  try {
    const result = await request.delete<boolean>(`/v1/industry/org/${orgId}?brandId=${brandId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

// excel表格导入组织
export const templateAddress = "/files/org_template.xlsx"
/**
 * @param file
 * @param brandId
 */
export async function importOrg(params: any): Promise<number> {
  try {
    const result = await request.post<number>("/v1/industry/org/import", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CompanyOutlineVO {
  userCount: number
  roleCount: number
  orgCount: number
}
// 获取公司概览
export async function getOutline(brandId: string): Promise<CompanyOutlineVO> {
  try {
    const result = await request.get<CompanyOutlineVO>("/v1/industry/company/outline", {
      params: {
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface MyAuthStrategyVO {
  // 公司id
  companyId: number
  // 角色权限信息列表
  roleAuths: string[]
  // 品牌类型
  industryCategory?: IndustryCategory
  // 组织id
  orgId?: number
}

// 获取公司本人的权限策略概况
export async function getUserAuth(brandId: string): Promise<MyAuthStrategyVO> {
  try {
    const result = await request.get<MyAuthStrategyVO>("/v1/industry/company/outline/userAuth", {
      params: {
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

// 删除用户
export async function deleteUser(username: string, brandId: string): Promise<ResultVO> {
  try {
    const result = await request.delete<ResultVO>(
      `/v2/industry/user/${username}?brandId=${brandId}`,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface EditUserArgs {
  username: string
  roleId: string
  orgId: string
  brandId: string
  // 数据权限
  userDataAuths: any[]
}
// 编辑用户
export async function editUserAuth(params: EditUserArgs): Promise<ResultVO> {
  try {
    const { username, brandId, ...rest } = params
    const result = await request.patch<ResultVO>(`/v2/industry/user/${username}/${brandId}`, {
      ...rest,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface UserItem {
  name: string
  username: string
  phone: string
  orgId: string
  roleId: string
  leaderName: string
  leaderUsername: string
  superAdmin: boolean
  leader: boolean
  createAt: string
}
// 列出可继承及用户权限的用户
export async function getLegalInheritorList(phone: string, brandId: string): Promise<UserItem[]> {
  try {
    const result = await request.get<UserItem[]>(
      `/v1/industry/user/legalInheritor/${phone}/${brandId}`,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface InheritUserCooStatusArgs {
  srcUsername: string
  desUsername: string
}
// 继承用户维护数据
export async function inheritUserCooStatus(params: InheritUserCooStatusArgs): Promise<ResultVO> {
  try {
    const result = await request.post<ResultVO>("/v2/industry/user/inheritCooStatus", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 生成邀请码
export async function getInviteCode(brandId: string): Promise<string> {
  try {
    const result = await request.get<string>(`/v2/industry/user/inviteCode?brandId=${brandId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface InviteUserArgs {
  name: string
  phone: string
  inviteCode: string
  orgId: string
  brandId: string
}
// 邀请添加用户
export async function inviteUser(params: InviteUserArgs): Promise<ResultVO> {
  try {
    const result = await request.post<ResultVO>("/v2/industry/user/inviteUser", params)
    return result?.data
  } catch (error: any) {
    throw error && error.response
  }
}

export interface DeptItemVO {
  pid?: string
  id: string
  name: string
  children?: DeptItemVO[]
}

interface OrgUser {
  id: string
  name: string
  phone: string
  username: string
}
export interface OrgTreeVO {
  id: string
  name: string
  users?: OrgUser
  leaderUsername?: string
  children?: OrgTreeVO[]
}
/**
 * 获取组织树
 * @param withUsers：是否返回组织中的用户，true-返回，false-不返回，非必传
 */

export async function getCompanyOrgTree(withUsers?: boolean): Promise<OrgTreeVO> {
  try {
    const result = await request.get<OrgTreeVO>("/v2/industry/org/tree", {
      params: {
        withUsers: withUsers ? true : false,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface OrgCreateDTO {
  // 品牌id
  brandId: string
  // 组织名
  name: string
  // 父节点id
  pid: string
}
// 创建组织
export async function createCompanyOrg(params: OrgCreateDTO): Promise<boolean> {
  try {
    const { brandId, ...others } = params
    const result = await request.post<boolean>(`/v2/industry/org?brandId=${brandId}`, others)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface OrgEditDTO {
  // 组织名，非必传，不能同时为空
  name?: string | null
  // 部门负责人手机号，非必传，不能同时为空
  leaderUsername?: string | null
  orgId: string
  brandId: string
}
// 编辑组织 & 设置负责人
export async function updateCompanyOrg({
  name = null,
  leaderUsername = null,
  orgId,
  brandId,
}: OrgEditDTO): Promise<boolean> {
  try {
    const result = await request.patch<boolean>(`/v2/industry/org/${orgId}?brandId=${brandId}`, {
      name,
      leaderUsername,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

// 删除组织
export async function deleteCompanyOrg(orgId: string, brandId: string): Promise<boolean> {
  try {
    const result = await request.delete<boolean>(`/v2/industry/org/${orgId}?brandId=${brandId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 根据邀请码获取组织树
export async function getCompanyOrgByCode(inviteCode: string, brandId: string): Promise<OrgTreeVO> {
  try {
    const result = await request.get<OrgTreeVO>(`/v2/industry/org/code/${inviteCode}`, {
      params: {
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface AncestorUsersArgs {
  keyword: string
  orgId: string
}
export interface OrgAncestorUserVO {
  id: string
  name: string
  username: string
  phone: string
}
// 查询同组和上级组织人员列表
export async function geTancestorUsers(params: AncestorUsersArgs): Promise<OrgAncestorUserVO[]> {
  try {
    const result = await request.get<OrgAncestorUserVO[]>("/v2/industry/org/ancestorUsers", {
      params: {
        ...params,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface CompanyOrgUserArgs {
  brandId: string
  orgId: string
  page: number
  limit: number
}
export interface CompanyUserV2VO {
  name: string
  username: string
  phone: string
  roleId: string
  roleName: string
  orgList: string
  orgId: string
  orgName: string
  leaderName: string
  leaderUsername: string
  leader: boolean
  cooStatusCount: number
  // 企业数量
  companyCooStatusCount: number
  // 数据权限
  dataAuths: DataAuthVO[]
}
interface CompanyUserListVO {
  list: CompanyUserV2VO[]
  total: number
}
// 获取公司用户列表
export async function getUserListByOrg(params: CompanyOrgUserArgs): Promise<CompanyUserListVO> {
  try {
    const { orgId, ...others } = params
    const result = await request.get<CompanyUserListVO>(`/v2/industry/user/${orgId}`, {
      params: {
        ...others,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * excel批量导入用户
 * @param orgId
 * @param companyId
 * @param file
 * @param brandId
 */
// 指定机构导入
export const userSingleTemplate = isNH
  ? "/files/user_template_single_nh.xlsx"
  : "/files/user_template_single.xlsx"

// 多机构导入
export const userMultipleTemplate = isNH
  ? "/files/user_template_multiple_nh.xlsx"
  : "/files/user_template_multiple.xlsx"

export async function importCompanyUser(params: any): Promise<ResultVO> {
  try {
    const orgId = params.get("orgId") || ""
    const result = await request.post<ResultVO>(`/v2/industry/user/import/${orgId}`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface MultipleCompanyOrgArgs {
  brandId: string
  desOrgId: string
  srcOrgIds: string[]
  usernames: string[]
}
// 批量修改用户信息
export async function updateMultipleCompanyOrg(params: MultipleCompanyOrgArgs): Promise<ResultVO> {
  const { brandId, ...rest } = params
  try {
    const result = await request.post<ResultVO>(`/v2/industry/user/orgInfo`, rest, {
      params: { brandId },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface QueryUserArgs {
  keyword: string
  companyId: number
  page: number
  limit: number
  brandId: string
}
// 搜索用户：name/username/phone
export async function queryCompanyUser(params: QueryUserArgs): Promise<CompanyUserListVO> {
  try {
    const result = await request.get<CompanyUserListVO>("/v2/industry/user/query", {
      params: {
        ...params,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface QueryUserOrgVO {
  treeVOS: OrgTreeVO[]
  userV2VOS: CompanyUserV2VO[]
}
// 搜索用户和组织
export async function queryUserOrg(keyword: string, brandId: string): Promise<QueryUserOrgVO> {
  try {
    const result = await request.get<QueryUserOrgVO>("/v2/industry/user/queryUserOrg", {
      params: {
        keyword,
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface UsernameVO {
  id: string
  name: string
  phone: string
  username: string
  org: {
    id: string
    name: string
    ancestorIds: string[]
  }
}
// 通过手机号查询用户
export async function getUsernameByPhone(phone: string): Promise<UsernameVO> {
  try {
    const result = await request.get<UsernameVO>(
      `${API_AUTH}/v1/admin/industry/users/by-phone/${phone}`,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface CompanyUserVO {
  userId: string
  userName: string
  phonenumber: string
}

// 通过手机号获取同公司用户
export async function queryUserByPhone(query: string, brandId: string): Promise<CompanyUserVO[]> {
  try {
    const result = await http.get<CompanyUserVO[]>(`/system/user/match`, {
      params: {
        query,
        brandId,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface DataAuthVO {
  // 权限ID
  id: string
  // 权限名称
  name: string
  // 子数据权限
  children?: DataAuthVO[]
}

interface AuthCompanyRoleVO {
  // 角色ID
  id: string
  // 角色名称
  name: string
  // 角色描述
  desc: string
  // 角色权限
  authStrategy: RoleAuthStrategy[]
  // 角色模块权限
  moduleTypes: IndustryModuleType[]
}

interface CompanyAuthVO {
  // 公司数据权限
  dataAuth: DataAuthVO
  // 公司角色
  roles: AuthCompanyRoleVO[]
}

/**
 * 获取公司组织权限
 */
export async function getCompanyAuth(brandId: string, showPoi = false): Promise<CompanyAuthVO> {
  try {
    const result = await request.post<CompanyAuthVO>("/v1/industry/company/auth", {
      brandId,
      showPoi,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface DataAuthCreateArg {
  // 品牌ID
  brandId: string
  // 父级权限ID
  pid: string
  // 数据权限名称
  dataAuthName: string
}

/**
 * 添加子数据权限
 */
export async function createCompanyDataAuth(params: DataAuthCreateArg): Promise<ResultVO> {
  try {
    const result = await request.put<ResultVO>("/v1/industry/company/dataAuth", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface DataAuthModifyArg {
  // 品牌ID
  brandId: string
  // 权限ID
  id: string
  // 数据权限名称
  dataAuthName: string
}

/**
 * 修改数据权限
 */
export async function updateCompanyDataAuth(params: DataAuthModifyArg): Promise<ResultVO> {
  try {
    const result = await request.patch<ResultVO>("/v1/industry/company/dataAuth", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface DataAuthDeleteArg {
  // 品牌ID
  brandId: string
  // 权限ID
  id: string
}

/**
 * 删除数据权限
 */
export async function deleteCompanyDataAuth(params: DataAuthDeleteArg): Promise<ResultVO> {
  try {
    const result = await request.delete<ResultVO>("/v1/industry/company/dataAuth", {
      data: { ...params },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface QueryDataAuthArgs {
  // 品牌ID
  brandId: string
  // 数据权限查询名称
  queryName: string
}

/**
 * 搜索数据权限
 */
export async function queryCompanyDataAuth(params: QueryDataAuthArgs): Promise<DataAuthVO[]> {
  try {
    const result = await request.post<DataAuthVO[]>("/v1/industry/company/dataAuth", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface PoiQueryArg {
  // 品牌ID
  brandId: string
  // 数据权限ID
  id: string
}

export interface DataAuthPoiVO {
  // POI ID
  poiId: string
  // POI名称
  poiName: string
  // 所在地区
  area: string
}

/**
 * 查询数据权限中的POI
 */
export async function getCompanyPoiList(params: PoiQueryArg): Promise<DataAuthPoiVO[]> {
  try {
    const result = await request.post<DataAuthPoiVO[]>(
      "/v1/industry/company/dataAuth/poiData",
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface PoiMoveArg {
  // 品牌ID
  brandId: string
  // 原权限ID
  srcId: string
  // 目标权限ID
  targetId: string
  // POI ID列表
  poiIds: string[]
}

/**
 * 移动数据权限中的POI
 */
export async function removeCompanyPois(params: PoiMoveArg): Promise<ResultVO> {
  try {
    const result = await request.patch<ResultVO>("/v1/industry/company/dataAuth/poiData", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface AuthStratVO {
  name: string
  desc: string
  value: string
}

/**
 * 获取全部权限策略
 */
export async function getRoleAuthStrategy(): Promise<AuthStratVO[]> {
  try {
    const result = await request.get<AuthStratVO[]>("/v1/industry/role/authStrategy")
    return result?.data
  } catch (error) {
    throw error
  }
}
